import type { Column, DataFrame, DataSource, Row } from "../types";

/**
 * Cache key for a resolved data source.
 *
 * The active connection is part of the key: the same SQL against a different
 * connector is a different answer, and without this a connector switch would
 * silently serve the previous one's rows.
 */
export function sourceKey(source: DataSource, connectionId: string | null): string {
  return `sql:${connectionId ?? "none"}:${source.query}`;
}

/**
 * Infer a display type per column from the values actually returned.
 *
 * The server sends JSON without type metadata, and the type drives alignment
 * and number formatting in tables. Inferred from the first non-null value:
 * scanning every row would cost a full pass over 100k rows for no extra
 * certainty, since a column with mixed types has no single display type anyway.
 */
export function inferColumns(names: string[], rows: Row[]): Column[] {
  return names.map((name) => {
    const sample = rows.find((r) => r[name] !== null && r[name] !== undefined)?.[name];
    let type: Column["type"] = "string";
    if (typeof sample === "number") {
      type = "number";
    } else if (typeof sample === "string" && ISO_DATE.test(sample)) {
      type = "date";
    }
    return { name, type };
  });
}

/** `2026-04-17` or `2026-04-17T09:30:00…` — the shapes our SQL emits. */
const ISO_DATE = /^\d{4}-\d{2}-\d{2}([T ]|$)/;

/** An empty frame, for the loading and error states. */
export const EMPTY_FRAME: DataFrame = { columns: [], rows: [] };
